import { memo, useCallback, useContext, useEffect, useRef, useState } from 'react';
import invariant from 'tiny-invariant';
import {
	type Instruction,
	type ItemMode,
} from '@atlaskit/pragmatic-drag-and-drop-hitbox/tree-item';
import { combine } from '@atlaskit/pragmatic-drag-and-drop/combine';
import {
	draggable,
	dropTargetForElements,
	monitorForElements,
} from '@atlaskit/pragmatic-drag-and-drop/element/adapter';
import { pointerOutsideOfPreview } from '@atlaskit/pragmatic-drag-and-drop/element/pointer-outside-of-preview';
import { setCustomNativeDragPreview } from '@atlaskit/pragmatic-drag-and-drop/element/set-custom-native-drag-preview';
import type { DragLocationHistory } from '@atlaskit/pragmatic-drag-and-drop/types';
import { createRoot } from 'react-dom/client';
import { useLocation } from 'react-router-dom';
import { OverlayTrigger, Tooltip } from 'react-bootstrap';
import { DependencyContext, TreeContext } from './TreeContext';
import { Step } from '@/backend/models/containers/step';
import { useGetFirstPendingStep } from '@/frontend/hooks/useGetFirstPendingStep';
import { useTreeStore } from '@/frontend/store/TreeStore';
import { DownCaret } from '@/frontend/SVGs/DownCaret';
import { RightCaret } from '@/frontend/SVGs/RightCaret';
import { ContainerTreeObject, TreeContextValue, DependencyContextType } from '@/frontend/types/navigationTypes';
import { I18n } from '@/i18n/I18n';
import { Dot } from '@/frontend/SVGs/Dot';
import { searchForNextStep } from '@/frontend/hooks/useGetNextStep';
import { scrollToElement } from '@/frontend/utils/scrollTo';
import { containerBackgroundColor, containerHasFirstIncompleteStep } from '@/frontend/utils/sideNavSectionColor';
import { Status } from '@/types/containerTypes';
import { CircleX } from '@/frontend/SVGs/CircleX';
import { Completed } from '@/frontend/SVGs/Completed';
import { Incomplete } from '@/frontend/SVGs/Incomplete';
import { BookmarkCheck } from '@/frontend/SVGs/BookmarkCheck';

export const statusIcon = {
  [Status.PENDING]: <div className='pending-icon'><Incomplete /></div>,
  [Status.PASSED]:  <div className='completion-icon'><Completed /></div>,
  [Status.FAILED]:  <div className='failed-icon'><CircleX /></div>,
  [Status.ABORTED]: <div className='aborted-icon'><CircleX /></div>,
  [Status.DEVIATED]: <div className='deviated-icon'><CircleX /></div>,
};

export const indentPerLevel = 26;
function GroupIcon({ isOpen }: { isOpen: boolean }) {
	return isOpen ? <DownCaret size='sm' /> : <RightCaret size='sm' />;
}

function Icon({ item, open }: { item: ContainerTreeObject, open: boolean }) {
	if (!item.children.filter((item) => item?.shouldRender).length) {
		return <Dot />;
	}
	return <GroupIcon isOpen={open ?? false} />;
}

function Preview({ item }: { item: ContainerTreeObject }) {
	return <div>Item {item.id}</div>;
}

function getParentLevelOfInstruction(instruction: Instruction): number {
	if (instruction.type === 'instruction-blocked') {
		return getParentLevelOfInstruction(instruction.desired);
	}
	if (instruction.type === 'reparent') {
		return instruction.desiredLevel - 1;
	}
	return instruction.currentLevel - 1;
}

function delay({ waitMs: timeMs, fn }: { waitMs: number; fn: () => void }): () => void {
	let timeoutId: number | null = window.setTimeout(() => {
		timeoutId = null;
		fn();
	}, timeMs);
	return function cancel() {
		if (timeoutId) {
			window.clearTimeout(timeoutId);
			timeoutId = null;
		}
	};
}

const TreeItem = memo(function TreeItem({
	item,
	mode,
	level,
	index,
  dragDisabled,
  hideProgress = false,
}: {
	item: ContainerTreeObject;
	mode: ItemMode;
	level: number;
	index: number;
  dragDisabled: boolean;
  hideProgress?: boolean;
}) {
	const buttonRef = useRef<HTMLDivElement>(null);
  const location = useLocation();
  // const navigate = useNavigate();
  // const procedure = useProcedureStore(state => state.procedure);

  function findFirstStep(container: ContainerTreeObject): ContainerTreeObject | undefined {
    if (container.container instanceof Step) return container;

    // if (container.children.length === 0 && location.pathname.includes('wizard')) {
    //   navigate(`/procedures/wizard/${procedure?.id}/steps/step-not-found/${container.id}`);
    //   return undefined;
    // }
    return searchForNextStep(container.children);
  }

  function onItemClick() {
    setSelectedId(item.id);
    if (location.pathname.includes('wizard')) {
      const firstStep = findFirstStep(item);
      if (firstStep) {
        // navigate(`/procedures/wizard/${procedure?.id}/steps/${firstStep.id}`);
        return;
      }
    }
    scrollToElement(item.id);
  }

	const [_state, setState] = useState<'idle' | 'dragging' | 'preview' | 'parent-of-instruction'>(
		'idle',
	);

  const [isOpen, setIsOpen] = useState<boolean>(false);
	const [instruction, setInstruction] = useState<Instruction | null>(null);
	const cancelExpandRef = useRef<(() => void) | null>(null);
  const setSelectedId = useTreeStore(state => state.setSelectedId);

	const { uniqueContextId, getPathToItem, registerTreeItem } = useContext<TreeContextValue>(TreeContext);
	const { DropIndicator, attachInstruction, extractInstruction } = useContext<DependencyContextType>(DependencyContext);
	const toggleOpen = useCallback(() => {
    setIsOpen(!isOpen);
	}, [isOpen]);

	useEffect(() => {
		invariant(buttonRef.current);

		return registerTreeItem({
			itemId: item.id,
			element: buttonRef.current,
		});
	}, [item.id, registerTreeItem]);

	const cancelExpand = useCallback(() => {
		cancelExpandRef.current?.();
		cancelExpandRef.current = null;
	}, []);

	const clearParentOfInstructionState = useCallback(() => {
		setState((current) => (current === 'parent-of-instruction' ? 'idle' : current));
	}, []);

	// When an item has an instruction applied
	// we are highlighting it's parent item for improved clarity
	const shouldHighlightParent = useCallback(
		(location: DragLocationHistory): boolean => {
			const target = location.current.dropTargets[0];

			if (!target) {
				return false;
			}

			const instruction = extractInstruction(target.data);

			if (!instruction) {
				return false;
			}

			const targetId = target.data.id;
			invariant(typeof targetId === 'string');

			const path = getPathToItem(targetId);
			const parentLevel: number = getParentLevelOfInstruction(instruction);
			const parentId = path[parentLevel];
			return parentId === item.id;
		},
		[getPathToItem, extractInstruction, item],
	);

	useEffect(() => {
		invariant(buttonRef.current);

		function updateIsParentOfInstruction({ location }: { location: DragLocationHistory }) {
			if (shouldHighlightParent(location)) {
				setState('parent-of-instruction');
				return;
			}
			clearParentOfInstructionState();
		}

		return combine(
			draggable({
				element: buttonRef.current,
				getInitialData: () => ({
					id: item.id,
					type: 'tree-item',
					isOpenOnDragStart: isOpen,
					uniqueContextId,
				}),
				onGenerateDragPreview: ({ nativeSetDragImage }) => {
					setCustomNativeDragPreview({
						getOffset: pointerOutsideOfPreview({ x: '16px', y: '8px' }),
						render: ({ container }) => {
							 
							const root = createRoot(container);
              root.render(<Preview item={item} />);
							 
							return () => root.unmount();
						},
						nativeSetDragImage,
					});
				},
				onDragStart: () => {
					setState('dragging');
					// collapse open items during a drag

				},
				onDrop: () => {
					setState('idle');
				},
			}),
			dropTargetForElements({
				element: buttonRef.current,
				getData: ({ input, element }) => {
					const data = { id: item.id };

					return attachInstruction(data, {
						input,
						element,
						indentPerLevel,
						currentLevel: level,
						mode,
						block: item.isDraft ? ['make-child'] : [],
					});
				},
				canDrop: ({ source }) =>
					source.data.type === 'tree-item' && source.data.uniqueContextId === uniqueContextId,
				getIsSticky: () => true,
				onDrag: ({ self, source }) => {
					const instruction = extractInstruction(self.data);
          if (source.data.id === item.id) setIsOpen(false);
					if (source.data.id !== item.id) {
						if (
							instruction?.type === 'make-child' &&
							item.children.length &&
							!isOpen &&
							!cancelExpandRef.current
						) {
							cancelExpandRef.current = delay({
								waitMs: 500,
								fn: () => {
                  setIsOpen(true);
                },
							});
						}
						if (instruction?.type !== 'make-child' && cancelExpandRef.current) {
							cancelExpand();
						}

						setInstruction(instruction);
						return;
					}
					if (instruction?.type === 'reparent') {
						setInstruction(instruction);
						return;
					}
					setInstruction(null);
				},
				onDragLeave: () => {
					cancelExpand();
					setInstruction(null);
				},
				onDrop: () => {
					cancelExpand();
					setInstruction(null);
				},
			}),
			monitorForElements({
				canMonitor: ({ source }) => source.data.uniqueContextId === uniqueContextId,
				onDragStart: updateIsParentOfInstruction,
				onDrag: updateIsParentOfInstruction,
				onDrop() {
					clearParentOfInstructionState();
				},
			}),
		);
	}, [
		item,
		mode,
		level,
		cancelExpand,
		uniqueContextId,
		extractInstruction,
		attachInstruction,
		getPathToItem,
		clearParentOfInstructionState,
		shouldHighlightParent,
    isOpen
	]);

	const aria = (() => {
		if (!item.children.length) {
			return undefined;
		}
		return {
			'aria-expanded': isOpen,
			'aria-controls': `tree-item-${item.id}--subtree`,
		};
	})();

  const firstIncompleteStep = useGetFirstPendingStep();

  if (item?.shouldRender === false) {
    return null;
  }

  const showPendingIcon = firstIncompleteStep?.id === item.id || containerHasFirstIncompleteStep(item, firstIncompleteStep);

	return (
		<div key={`${item.id}-tree-item`}>
      <div className="position-relative" style={{ paddingLeft: level * indentPerLevel }} ref={buttonRef}>
        <div draggable={dragDisabled} className="d-flex py-1" id={`tree-item-${item.id}`}>
          <button
            {...aria}
            aria-label={I18n.get('navigationTreeItemLabel', {item: item.container.title || ''})}
            onClick={toggleOpen}
            type="button"
            className="d-flex border-0 bg-transparent h-10 rounded"
            data-index={index}
            data-level={level}
          >
            <Icon item={item} open={isOpen} />
          </button>
          <div className={`d-flex gap-1 rounded-1 px-1 ${!hideProgress ? containerBackgroundColor(item) : ''}`}>
            <button type='button' className='btn p-0 btn-link link-secondary text-decoration-none d-flex' draggable={dragDisabled} onClick={() => onItemClick()}>
              { !hideProgress && item.container instanceof Step && <div className='pe-2'>{statusIcon[item.container.status]}</div> }
              { (item.wbs || item.container.title) &&
                <p className='text-start fs-6 mb-0' style={{ textIndent: '-0.5em', paddingLeft: '0.5em' }}>
                  {item.wbs &&
                    <>
                      <span className="fw-bold text-decoration-none text-nowrap text-dark">{`${item.wbs}`}</span>
                      { item.container.title && <span className='text-dark'> - </span>}
                    </>
                  }
                  <span className="text-decoration-underline text-secondary text-start">{item.container.title}</span>
                </p>
              }
              {(showPendingIcon && !isOpen && !hideProgress) &&
                <OverlayTrigger
                  placement="top"
                  delay={{ show: 250, hide: 400 }}
                  overlay={<Tooltip id="tooltip-top">{I18n.get('currentPlace', {model: item.container.containerType})}</Tooltip>}
                >
                  <span className='text-primary'>
                    <BookmarkCheck />
                  </span>
                </OverlayTrigger>
              }
            </button>
          </div>
        </div>
        {instruction ? <DropIndicator instruction={instruction} /> : null}
			</div>
			{item.children.length > 0 ? (
				<div data-testid="childTreeItem" id={aria?.['aria-controls']} className={isOpen ? '' : 'd-none'}>
					{item.children.map((child, index, array) => {
						const childType: ItemMode = (() => {
							if (child.children.length && child.isOpen) {
								return 'expanded';
							}

							if (index === array.length - 1) {
								return 'last-in-group';
							}

							return 'standard';
						})();
            if (!child.shouldRender) {
              return null;
            }
						return (
							<TreeItem
								item={child}
								key={child.id}
								level={level + 1}
								mode={childType}
								index={index}
                dragDisabled={dragDisabled}
                hideProgress={hideProgress}
							/>
						);
					})}
				</div>
			) : null}
		</div>
	);
});

export default TreeItem;
